import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ZIRVEGO_PAYMENT_METHODS,
  ZirveGoSendDraft,
  courierModuleService,
  formatMoney,
  toApiSource,
} from './courierModuleService';

interface Props {
  open: boolean;
  branchId: string;
  order: any | null;
  partNo?: number;
  partCount?: number;
  onClose: () => void;
  /** Zirve Go siparişi kabul etti: confirmationId ile atama sonrası teslimata bağlanır. */
  onSent: (confirmationId: string, externalOrderId: string) => void;
}

type Field = 'customerName' | 'phone' | 'address';
const REQUIRED: Field[] = ['customerName', 'phone', 'address'];

/**
 * Zirve Go'ya gönder (FiyuuSendDialog ile aynı düzen): siparişten doldurulan müşteri, adres ve ödeme kontrol edilir.
 * Ürünler ve tutarlar siparişten sunucuda okunur (burada yalnız gösterilir). Zirve Go reddederse hiçbir şey
 * değişmez; kabul ederse sipariş Zirve Go kuryesine atanır.
 */
const ZirveGoSendDialog: React.FC<Props> = ({ open, branchId, order, partNo = 1, partCount = 1, onClose, onSent }) => {
  const { t } = useTranslation('salesSystem');
  const [draft, setDraft] = useState<ZirveGoSendDraft | null>(null);
  const [doNotKnock, setDoNotKnock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    setDraft(null);
    setError(null);
    setTouched(false);
    setDoNotKnock(false);
    setLoading(true);
    courierModuleService.zirveGoDraft(branchId, toApiSource(order.source), Number(order.id))
      .then(setDraft)
      .catch((e: any) => setError(e?.message || t('courierModule.zirvego.loadFailed')))
      .finally(() => setLoading(false));
  }, [open, order, branchId, t]);

  const set = (patch: Partial<ZirveGoSendDraft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const missing = useMemo(() => {
    if (!draft) return [] as Field[];
    return REQUIRED.filter((f) => {
      const v = String((draft as any)[f] ?? '').trim();
      if (f === 'phone') return v.replace(/\D/g, '').length < 7;
      if (f === 'customerName') return v.length < 2;
      return v.length < 5;
    });
  }, [draft]);

  const send = async () => {
    setTouched(true);
    if (!draft || missing.length > 0) return;
    setSending(true);
    setError(null);
    try {
      const res = await courierModuleService.zirveGoSend({
        branchId,
        orderSource: draft.orderSource,
        orderRef: draft.orderRef,
        partNo,
        partCount,
        customerName: draft.customerName,
        phone: draft.phone,
        address: draft.address,
        addressDescription: draft.addressDescription || null,
        latitude: draft.latitude ?? null,
        longitude: draft.longitude ?? null,
        paymentMethod: draft.paymentMethod,
        note: draft.note || null,
        doNotKnock,
      });
      onSent(res.confirmationId, res.externalOrderId);
    } catch (e: any) {
      setError(e?.message || t('courierModule.zirvego.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const field = (f: Field, extra?: Partial<React.ComponentProps<typeof TextField>>) => (
    <TextField size="small" fullWidth label={t(`courierModule.zirvego.fields.${f}`)} value={(draft as any)?.[f] ?? ''}
      onChange={(e) => set({ [f]: e.target.value } as any)}
      error={touched && missing.includes(f)} required {...extra} />
  );

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" component="span">{t('courierModule.zirvego.title')}</Typography>
        {order && <Chip size="small" label={order.orderNumber || `#${order.id}`} />}
        {draft && <Chip size="small" color={draft.environment === 'live' ? 'success' : 'default'}
          label={draft.environment === 'live' ? t('courierModule.zirvego.live') : t('courierModule.zirvego.test')} />}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {draft && !draft.connectionReady && <Alert severity="warning" sx={{ mb: 1.5 }}>{draft.connectionProblem}</Alert>}
        {draft?.previousExternalOrderId && draft.previousStatus === 'order-canceled' && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {t('courierModule.zirvego.previouslyCancelled', {
              time: draft.previousStatusAt
                ? new Date(draft.previousStatusAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                : '—',
            })}
          </Alert>
        )}
        {draft?.previousExternalOrderId && draft.previousStatus !== 'order-canceled' && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>{t('courierModule.zirvego.alreadySent', { id: draft.previousExternalOrderId })}</Alert>
        )}
        {draft?.warnings.map((w) => <Alert key={w} severity="info" sx={{ mb: 1.5 }}>{w}</Alert>)}
        {draft && (
          <Stack spacing={2}>
            <Typography variant="overline" color="text.secondary">{t('courierModule.zirvego.customer')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              {field('customerName')}
              {field('phone', { helperText: t('courierModule.zirvego.phoneHint') })}
            </Box>

            <Typography variant="overline" color="text.secondary">{t('courierModule.zirvego.address')}</Typography>
            {field('address', { multiline: true, minRows: 2 })}
            <TextField size="small" label={t('courierModule.zirvego.fields.addressDescription')} value={draft.addressDescription ?? ''}
              onChange={(e) => set({ addressDescription: e.target.value })} />
            <Typography variant="caption" color="text.secondary">
              {draft.latitude != null && draft.longitude != null
                ? t('courierModule.zirvego.location', { lat: draft.latitude.toFixed(5), lng: draft.longitude.toFixed(5) })
                : t('courierModule.zirvego.noLocation')}
            </Typography>

            <Typography variant="overline" color="text.secondary">{t('courierModule.zirvego.products')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('courierModule.zirvego.cols.product')}</TableCell>
                  <TableCell align="right">{t('courierModule.zirvego.cols.count')}</TableCell>
                  <TableCell align="right">{t('courierModule.zirvego.cols.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {draft.lines.map((l, i) => (
                  <TableRow key={`${l.name}-${i}`}>
                    <TableCell>
                      {l.name}
                      {l.options.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">{l.options.join(' · ')}</Typography>
                      )}
                      {l.note && <Typography variant="caption" color="text.secondary" display="block">{l.note}</Typography>}
                    </TableCell>
                    <TableCell align="right">{l.count}</TableCell>
                    <TableCell align="right">{formatMoney(l.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="overline" color="text.secondary">{t('courierModule.zirvego.payment')}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField select size="small" label={t('courierModule.zirvego.fields.paymentMethod')} value={draft.paymentMethod}
                onChange={(e) => set({ paymentMethod: Number(e.target.value) })} sx={{ minWidth: 240 }}>
                {ZIRVEGO_PAYMENT_METHODS.map((p) => <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>)}
              </TextField>
              <Typography variant="body1" fontWeight={700}>{formatMoney(draft.totalDiscountedPrice)}</Typography>
              {draft.totalDiscount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('courierModule.zirvego.discount', { total: formatMoney(draft.totalPrice), discount: formatMoney(draft.totalDiscount) })}
                </Typography>
              )}
              {draft.isPaid && <Chip size="small" color="success" label={t('courierModule.zirvego.paid')} />}
            </Stack>
            <TextField size="small" label={t('courierModule.zirvego.fields.note')} value={draft.note ?? ''} onChange={(e) => set({ note: e.target.value })} />
            <FormControlLabel control={<Switch checked={doNotKnock} onChange={(e) => setDoNotKnock(e.target.checked)} />}
              label={t('courierModule.zirvego.doNotKnock')} />

            {touched && missing.length > 0 && (
              <Alert severity="error">{t('courierModule.zirvego.missing', { fields: missing.map((f) => t(`courierModule.zirvego.fields.${f}`)).join(', ') })}</Alert>
            )}
            <Typography variant="caption" color="text.secondary">{t('courierModule.zirvego.hint')}</Typography>
          </Stack>
        )}
      </DialogContent>
      {error && <Alert severity="error" sx={{ mx: 3, mt: 1.5 }}>{error}</Alert>}
      <DialogActions>
        <Button disabled={sending} onClick={onClose}>{t('courierModule.zirvego.cancel')}</Button>
        <Button variant="contained" disabled={sending || !draft?.connectionReady} onClick={send}>
          {sending ? <CircularProgress size={18} color="inherit" /> : t('courierModule.zirvego.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZirveGoSendDialog;
